// ✅ AdminCareerTracks (ALTERED ONLY FOR THEME + endpoint invocation style)
// - Matches your “above theme”: orange header bar + white title, BG #F2F2F2, soft blue borders
// - Inputs: soft blue border + orange focus ring
// - Buttons: primary orange, outlined blue (and secondary blue buttons inside dialog)
// - Surfaces: white cards with soft border, zebra/hover
// - Dialog: gradient title bar
// - Backend calls invoked like your CodeSubCampus page (inline headers in each call; no memo headers)
// - No business/API logic changed

import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  Alert,
  Divider,
  Stack,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Chip,
} from "@mui/material";
import { API_URL } from "./config";

const LEVELS = ["beginner", "internship_ready", "job_ready", "advanced"];
const TIERS = ["dream", "target", "safe"];

export default function AdminCareerTracks() {
  // ✅ Theme constants (same pattern as your “above theme”)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG, minHeight: "100vh" };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };
  const bodySx = { p: 2, backgroundColor: BG };
  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const blueBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: "rgba(25,118,210,0.95)",
    "&:hover": { backgroundColor: "rgba(20,90,160,1)" },
  };

  const chipSx = {
    bgcolor: "rgba(25,118,210,0.06)",
    border: "1px solid rgba(25,118,210,0.16)",
    fontWeight: 800,
    mb: 1,
  };

  const zebraCardSx = (idx) => ({
    ...surfaceSx,
    p: 1.5,
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#fff",
    "&:hover": { backgroundColor: ROW_HOVER },
  });

  // ✅ Token (same pattern as before; fallback supported)
  const token = localStorage.getItem("auth") || localStorage.getItem("token");

  const [error, setError] = useState("");
  const [tracks, setTracks] = useState([]);
  const [interestAreas, setInterestAreas] = useState([]);
  const [skills, setSkills] = useState([]);

  // create track
  const [newTrack, setNewTrack] = useState({
    InterestAreaId: "",
    TrackName: "",
    TrackKey: "",
    IsActive: 1,
  });

  // details dialog
  const [open, setOpen] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [details, setDetails] = useState(null);

  // add skill row
  const [skillLevel, setSkillLevel] = useState("beginner");
  const [skillPick, setSkillPick] = useState(null);
  const [skillPriority, setSkillPriority] = useState(3);
  const [skillNotes, setSkillNotes] = useState("");

  // add tool row
  const [toolLevel, setToolLevel] = useState("beginner");
  const [toolName, setToolName] = useState("");
  const [toolType, setToolType] = useState("tool");
  const [toolLink, setToolLink] = useState("");
  const [toolNotes, setToolNotes] = useState("");

  // add company row
  const [tierKey, setTierKey] = useState("dream");
  const [companyName, setCompanyName] = useState("");
  const [companyNotes, setCompanyNotes] = useState("");

  const load = async () => {
    setError("");
    try {
      const [t, ia, sk] = await Promise.all([
        axios.get(`${API_URL}/api/career/admin/tracks`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/career/interest-areas`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/career/skills`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setTracks(t.data || []);
      setInterestAreas(ia.data || []);
      setSkills(sk.data || []);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to load admin career tracks");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createTrack = async () => {
    setError("");
    try {
      await axios.post(`${API_URL}/api/career/admin/tracks`, newTrack, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNewTrack({ InterestAreaId: "", TrackName: "", TrackKey: "", IsActive: 1 });
      await load();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to create track");
    }
  };

  const openDetails = async (track) => {
    setSelectedTrack(track);
    setOpen(true);
    setDetails(null);
    setError("");
    try {
      const resp = await axios.get(`${API_URL}/api/career/admin/tracks/${track.TrackId}/details`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDetails(resp.data);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to load track details");
    }
  };

  const addTrackSkill = async () => {
    if (!selectedTrack || !skillPick) return;
    setError("");
    try {
      await axios.post(
        `${API_URL}/api/career/admin/tracks/${selectedTrack.TrackId}/skills`,
        {
          LevelKey: skillLevel,
          SkillId: skillPick.SkillId,
          Priority: skillPriority,
          Notes: skillNotes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSkillPick(null);
      setSkillPriority(3);
      setSkillNotes("");
      await openDetails(selectedTrack);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to add track skill");
    }
  };

  const removeTrackSkill = async (trackSkillId) => {
    setError("");
    try {
      await axios.delete(
        `${API_URL}/api/career/admin/tracks/${selectedTrack.TrackId}/skills/${trackSkillId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await openDetails(selectedTrack);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to remove skill");
    }
  };

  const addTool = async () => {
    if (!selectedTrack || !toolName) return;
    setError("");
    try {
      await axios.post(
        `${API_URL}/api/career/admin/tracks/${selectedTrack.TrackId}/tools`,
        {
          LevelKey: toolLevel,
          ToolName: toolName,
          ToolType: toolType,
          LinkUrl: toolLink,
          Notes: toolNotes,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setToolName("");
      setToolLink("");
      setToolNotes("");
      await openDetails(selectedTrack);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to add tool");
    }
  };

  const removeTool = async (toolId) => {
    setError("");
    try {
      await axios.delete(`${API_URL}/api/career/admin/tracks/${selectedTrack.TrackId}/tools/${toolId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await openDetails(selectedTrack);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to delete tool");
    }
  };

  const addCompany = async () => {
    if (!selectedTrack || !companyName) return;
    setError("");
    try {
      await axios.post(
        `${API_URL}/api/career/admin/tracks/${selectedTrack.TrackId}/companies`,
        { TierKey: tierKey, CompanyName: companyName, Notes: companyNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCompanyName("");
      setCompanyNotes("");
      await openDetails(selectedTrack);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to add company");
    }
  };

  const removeCompany = async (trackCompanyId) => {
    setError("");
    try {
      await axios.delete(
        `${API_URL}/api/career/admin/tracks/${selectedTrack.TrackId}/companies/${trackCompanyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await openDetails(selectedTrack);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to delete company");
    }
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Admin: Career Tracks Builder
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Create tracks, then manage skills/tools/companies per level
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="outlined" sx={outlinedBtnSx} onClick={load}>
              Refresh
            </Button>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
                mb: 2,
              }}
            >
              {error}
            </Alert>
          )}

          {/* ✅ Create Track */}
          <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
            <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)", mb: 1 }}>
              Create Track
            </Typography>

            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <FormControl sx={{ minWidth: 240 }} size="small">
                <InputLabel>Interest Area</InputLabel>
                <Select
                  label="Interest Area"
                  value={newTrack.InterestAreaId}
                  onChange={(e) => setNewTrack((p) => ({ ...p, InterestAreaId: e.target.value }))}
                  sx={inputSx}
                >
                  {interestAreas.map((i) => (
                    <MenuItem key={i.InterestAreaId} value={i.InterestAreaId}>
                      {i.InterestLabel}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                size="small"
                label="Track Name (e.g., Frontend Developer)"
                value={newTrack.TrackName}
                onChange={(e) => setNewTrack((p) => ({ ...p, TrackName: e.target.value }))}
                sx={inputSx}
              />

              <TextField
                fullWidth
                size="small"
                label="Track Key (e.g., frontend_dev)"
                value={newTrack.TrackKey}
                onChange={(e) => setNewTrack((p) => ({ ...p, TrackKey: e.target.value }))}
                sx={inputSx}
              />

              <FormControl sx={{ minWidth: 140 }} size="small">
                <InputLabel>Active</InputLabel>
                <Select
                  label="Active"
                  value={newTrack.IsActive}
                  onChange={(e) => setNewTrack((p) => ({ ...p, IsActive: Number(e.target.value) }))}
                  sx={inputSx}
                >
                  <MenuItem value={1}>Yes</MenuItem>
                  <MenuItem value={0}>No</MenuItem>
                </Select>
              </FormControl>

              <Button variant="contained" onClick={createTrack} sx={primaryBtnSx}>
                Create
              </Button>
            </Stack>
          </Paper>

          <Divider sx={{ my: 2 }} />

          {/* ✅ Tracks list */}
          <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>Tracks</Typography>

          <Stack spacing={1.2} sx={{ mt: 1.5 }}>
            {tracks.map((t, idx) => (
              <Paper key={t.TrackId} elevation={0} sx={zebraCardSx(idx)}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }} noWrap>
                      {t.TrackName} {t.IsActive ? "" : "(Inactive)"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Interest: <b>{t.InterestLabel}</b> • TrackKey: <b>{t.TrackKey}</b>
                    </Typography>
                  </Box>

                  <Button variant="outlined" onClick={() => openDetails(t)} sx={outlinedBtnSx}>
                    Manage
                  </Button>
                </Stack>
              </Paper>
            ))}
            {tracks.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No tracks yet.
              </Alert>
            ) : null}
          </Stack>
        </Box>
      </Paper>

      {/* ✅ DETAILS */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: 900,
            color: "white",
            background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
          }}
        >
          Manage Track: {selectedTrack?.TrackName || ""}
        </DialogTitle>

        <DialogContent dividers sx={{ backgroundColor: BG }}>
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
                mb: 2,
              }}
            >
              {error}
            </Alert>
          )}

          {!details ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Loading details...
            </Alert>
          ) : (
            <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
              {/* Skills */}
              <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>Track Skills</Typography>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 1 }}>
                <FormControl sx={{ minWidth: 220 }} size="small">
                  <InputLabel>Level</InputLabel>
                  <Select label="Level" value={skillLevel} onChange={(e) => setSkillLevel(e.target.value)} sx={inputSx}>
                    {LEVELS.map((l) => (
                      <MenuItem key={l} value={l}>
                        {l}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Autocomplete
                  sx={{ flex: 1 }}
                  options={skills}
                  value={skillPick}
                  onChange={(e, v) => setSkillPick(v)}
                  getOptionLabel={(o) => `${o.SkillName} (${o.SkillCategory})`}
                  renderInput={(params) => <TextField {...params} size="small" label="Skill" sx={inputSx} />}
                />

                <TextField
                  sx={{ width: 140, ...inputSx }}
                  size="small"
                  label="Priority (1..5)"
                  type="number"
                  value={skillPriority}
                  onChange={(e) => setSkillPriority(Number(e.target.value))}
                />

                <Button variant="contained" onClick={addTrackSkill} sx={blueBtnSx}>
                  Add Skill
                </Button>
              </Stack>

              <TextField
                sx={{ mt: 1, ...inputSx }}
                size="small"
                fullWidth
                label="Notes (optional)"
                value={skillNotes}
                onChange={(e) => setSkillNotes(e.target.value)}
              />

              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
                {details.skills.map((s) => (
                  <Chip
                    key={s.TrackSkillId}
                    label={`${s.LevelKey}: ${s.SkillName} (P${s.Priority})`}
                    onDelete={() => removeTrackSkill(s.TrackSkillId)}
                    sx={chipSx}
                  />
                ))}
                {details.skills.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No skills added yet.
                  </Alert>
                ) : null}
              </Stack>

              <Divider sx={{ my: 2 }} />

              {/* Tools */}
              <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>Tools</Typography>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 1 }}>
                <FormControl sx={{ minWidth: 220 }} size="small">
                  <InputLabel>Level</InputLabel>
                  <Select label="Level" value={toolLevel} onChange={(e) => setToolLevel(e.target.value)} sx={inputSx}>
                    {LEVELS.map((l) => (
                      <MenuItem key={l} value={l}>
                        {l}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  size="small"
                  label="Tool Name"
                  value={toolName}
                  onChange={(e) => setToolName(e.target.value)}
                  sx={inputSx}
                />

                <TextField
                  sx={{ width: 160, ...inputSx }}
                  size="small"
                  label="Tool Type"
                  value={toolType}
                  onChange={(e) => setToolType(e.target.value)}
                />

                <Button variant="contained" onClick={addTool} sx={blueBtnSx}>
                  Add Tool
                </Button>
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Link (optional)"
                  value={toolLink}
                  onChange={(e) => setToolLink(e.target.value)}
                  sx={inputSx}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Notes (optional)"
                  value={toolNotes}
                  onChange={(e) => setToolNotes(e.target.value)}
                  sx={inputSx}
                />
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
                {details.tools.map((t) => (
                  <Chip
                    key={t.ToolId}
                    label={`${t.LevelKey}: ${t.ToolName}`}
                    onDelete={() => removeTool(t.ToolId)}
                    sx={chipSx}
                  />
                ))}
                {details.tools.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No tools added yet.
                  </Alert>
                ) : null}
              </Stack>

              <Divider sx={{ my: 2 }} />

              {/* Companies */}
              <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>Companies</Typography>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: 1 }}>
                <FormControl sx={{ minWidth: 220 }} size="small">
                  <InputLabel>Tier</InputLabel>
                  <Select label="Tier" value={tierKey} onChange={(e) => setTierKey(e.target.value)} sx={inputSx}>
                    {TIERS.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  size="small"
                  label="Company Name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  sx={inputSx}
                />

                <Button variant="contained" onClick={addCompany} sx={blueBtnSx}>
                  Add Company
                </Button>
              </Stack>

              <TextField
                sx={{ mt: 1, ...inputSx }}
                size="small"
                fullWidth
                label="Notes (optional)"
                value={companyNotes}
                onChange={(e) => setCompanyNotes(e.target.value)}
              />

              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
                {details.companies.map((c) => (
                  <Chip
                    key={c.TrackCompanyId}
                    label={`${c.TierKey}: ${c.CompanyName}`}
                    onDelete={() => removeCompany(c.TrackCompanyId)}
                    sx={chipSx}
                  />
                ))}
                {details.companies.length === 0 ? (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    No companies added yet.
                  </Alert>
                ) : null}
              </Stack>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, backgroundColor: BG }}>
          <Button onClick={() => setOpen(false)} sx={outlinedBtnSx}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
